import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { refreshSession, setSessionActive } from "@/lib/api";

// Strip trailing slashes: "https://host/" + "/api" would become "//api" and miss the API router.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const API_BASE = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user states:
  //   undefined → still checking session
  //   null      → not authenticated
  //   {object}  → authenticated user
  const [user, setUserState] = useState(undefined);
  // Fix 7 (B8): keep api.js informed so a 401 of a logged-in user triggers a refresh, and a failed
  // refresh sends the user to /login ("Su sesión expiró") keeping where they were.
  const setUser = (u) => {
    setSessionActive(!!u, () => {
      setUserState(null);
      const here = `${window.location.pathname}${window.location.search}`;
      window.location.assign(`/login?expirada=1&from=${encodeURIComponent(here)}`);
    });
    setUserState(u);
  };

  // Bootstrap: check existing session via cookie (access cookie expired → try the refresh cookie)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/auth/me`, {
          withCredentials: true,
        });
        if (!cancelled) setUser(data.user || null);
      } catch (_e) {
        let u = null;
        try {
          u = await refreshSession();
        } catch {
          u = null;
        }
        if (!cancelled) setUser(u);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async ({ email, password }) => {
    const { data } = await axios.post(
      `${API_BASE}/auth/login`,
      { email, password },
      { withCredentials: true },
    );
    if (!data?.user) {
      throw new Error("Respuesta inesperada del servidor: no se pudo iniciar sesión. Verifique la configuración del backend.");
    }
    setUser(data.user);
    return data;
  };

  const register = async ({ email, password, name, company }) => {
    const { data } = await axios.post(
      `${API_BASE}/auth/register`,
      { email, password, name, company },
      { withCredentials: true },
    );
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/auth/logout`, {}, { withCredentials: true });
    } catch (_e) {
      /* ignore */
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function formatAuthError(detail) {
  if (detail == null) return "Algo salió mal. Intente de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
